import { Search as SearchIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import React from "react";

import { Suggestion, SuggestionGroup } from "../../types/productPlane";
import { monospaceStyle } from "../../theme";
import { CountryFlag } from "../common/CountryFlag";
import { formatFilterCount } from "../Filters/FilterRow";
import { EXPLORE_FILTER_LABELS } from "./exploreFilters";

// One suggestion with its group, as the list is walked by the keyboard
export interface FlatSuggestion {
  group: SuggestionGroup["name"];
  suggestion: Suggestion;
}

// The suggestions of every group, in the order shown
export const flattenSuggestions = (
  groups: readonly SuggestionGroup[],
): FlatSuggestion[] =>
  groups.flatMap((group) =>
    group.suggestions.map((suggestion) => ({ group: group.name, suggestion })),
  );

// The id of an option, for the field's active descendant
export const suggestionOptionId = (listId: string, index: number): string =>
  `${listId}-option-${index}`;

// The heading of a group: the label of its filter, or "Products"
export const suggestionGroupLabel = (group: SuggestionGroup["name"]): string =>
  group === "product" ? "Products" : EXPLORE_FILTER_LABELS[group];

// What a count stands for in a group: products, or a product's documents
const countTitle = (group: SuggestionGroup["name"]): string =>
  group === "product" ? "Documents that mention it" : "Products";

interface ExploreSuggestionsProps {
  // The id of the list box, which the field names as the list it controls
  id: string;
  groups: readonly SuggestionGroup[];
  // The position, in `flattenSuggestions`, of the option the keyboard is on
  active: number | null;
  onPick: (picked: FlatSuggestion) => void;
  // The text a press of Enter searches for when no option is chosen
  text: string;
  // Searches for the text, as Enter does; on a click of the line that says so
  onSearch: () => void;
}

// The option the left or right arrow moves to from `active`: the one in the
// same row of the group beside it, or the last of a shorter group. Null at
// the first or last group, where the arrow has nowhere to go.
export const sideSuggestion = (
  groups: readonly SuggestionGroup[],
  active: number,
  step: 1 | -1,
): number | null => {
  let start = 0;
  for (let group = 0; group < groups.length; group += 1) {
    const size = groups[group].suggestions.length;
    if (active < start + size) {
      const target = groups[group + step];
      if (!target) {
        return null;
      }
      const targetStart =
        step > 0 ? start + size : start - target.suggestions.length;
      return (
        targetStart + Math.min(active - start, target.suggestions.length - 1)
      );
    }
    start += size;
  }
  return null;
};

// The suggestions under Explore's search field. On top the line that says
// Enter searches for the text, marked while no option is chosen; under it
// the suggested filters, a column per group side by
// side: a heading, an option per suggestion with its count, a flag before a
// country, and the other name the text met beside the name. The columns
// wrap to a further row when the field is narrow. The field keeps the
// focus: an option is picked on a press of the mouse, before the field
// would blur.
const ExploreSuggestions: React.FC<ExploreSuggestionsProps> = ({
  id,
  groups,
  active,
  onPick,
  text,
  onSearch,
}) => {
  let position = -1;
  return (
    <Box>
      {/* What Enter does while no option is chosen, marked as such */}
      <Box
        onMouseDown={(event) => {
          // Keep the focus in the field
          event.preventDefault();
          onSearch();
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          minHeight: 36,
          cursor: "pointer",
          backgroundColor: active === null ? "action.selected" : undefined,
          "&:hover": {
            backgroundColor: active === null ? "action.selected" : "action.hover",
          },
        }}
      >
        <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography
          component="span"
          variant="body2"
          sx={{
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {`Press Enter to search for “${text.trim()}”`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          pt: 1,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Suggested filters
        </Typography>
        <Typography variant="caption" color="text.disabled">
          ↑↓ ←→ to choose
        </Typography>
      </Box>
      <Box
        id={id}
        role="listbox"
        aria-label="Suggestions"
        sx={{
          maxHeight: 420,
          overflowY: "auto",
          py: 0.5,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          alignItems: "start",
          columnGap: 0.5,
          rowGap: 1,
        }}
      >
        {groups.map((group) => (
          <Box
            key={group.name}
            role="group"
            aria-label={suggestionGroupLabel(group.name)}
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              aria-hidden
              sx={{ px: 1.5, pt: 0.75, pb: 0.25, fontWeight: 600 }}
            >
              {suggestionGroupLabel(group.name)}
            </Typography>
            {group.suggestions.map((suggestion) => {
              position += 1;
              const index = position;
              const selected = index === active;
              return (
                <Box
                  key={suggestion.value}
                  id={suggestionOptionId(id, index)}
                  role="option"
                  aria-selected={selected}
                  onMouseDown={(event) => {
                    // Keep the focus in the field
                    event.preventDefault();
                    onPick({ group: group.name, suggestion });
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    minHeight: 30,
                    cursor: "pointer",
                    // A pick that would list nothing under the selection
                    // stays offered, dimmed
                    opacity: suggestion.count === 0 ? 0.5 : 1,
                    backgroundColor: selected ? "action.selected" : undefined,
                    "&:hover": {
                      backgroundColor: selected ? "action.selected" : "action.hover",
                    },
                  }}
                >
                  {(suggestion.group ?? null) === "country" && (
                    <CountryFlag code={suggestion.value} />
                  )}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {suggestion.name}
                    {suggestion.matched_name && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 0.75 }}
                      >
                        {suggestion.matched_name}
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    title={countTitle(group.name)}
                    sx={{ ...monospaceStyle, flexShrink: 0 }}
                  >
                    {formatFilterCount(suggestion.count)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ExploreSuggestions;
